import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClinicCtx } from '@/app/state';
import { Button, Field, Input, Select, Textarea } from '@/components/ui';
import { registerPatient, updatePatient, getPatientIndex, type NewPatient, type PatientForEdit } from './repo';
import type { PatientIndexRow } from '@/lib/db/schema';
import { normalizePhone, formatPhoneLocal } from '@/lib/myanmar/phone';
import { parseNrc } from '@/lib/myanmar/nrc';
import { toUnicode } from '@/lib/myanmar/zawgyi';

const HONORIFICS = ['none', 'U', 'Daw', 'Ko', 'Ma', 'Maung', 'Dr', 'Sayar', 'Sayarma'];

type FormState = { honorific: string; name_my: string; name_en: string; sex: string; dob: string; age_years: string; age_months: string; approximate: boolean; phone: string; viber: boolean; nrc: string; address_line: string; allergies: string; notes: string; consent: boolean };

function fromEdit(p: PatientForEdit): FormState {
  return {
    honorific: p.honorific ?? 'none', name_my: p.name_my ?? '', name_en: p.name_en ?? '', sex: p.sex, dob: p.dob ?? '',
    age_years: p.age_years != null ? String(p.age_years) : '', age_months: p.age_months != null ? String(p.age_months) : '', approximate: !!p.age_approximate,
    phone: p.phones[0] ? formatPhoneLocal(p.phones[0].phone) : '', viber: !!p.phones[0]?.viber, nrc: p.nrc ?? '', address_line: p.address_line ?? '',
    allergies: (p.allergies ?? []).map((a) => a.substance).join(', '), notes: p.notes ?? '', consent: !!p.consent_messaging,
  };
}

export function RegisterPatientForm({ prefix, initial, prefill, onDone }: { prefix: string; initial?: PatientForEdit; prefill?: { phone?: string; name_my?: string; name_en?: string }; onDone: (p: PatientIndexRow | null) => void }) {
  const { t } = useTranslation();
  const ctx = useClinicCtx();
  const [f, setF] = useState<FormState>(initial ? fromEdit(initial) : { honorific: 'none', name_my: prefill?.name_my ?? '', name_en: prefill?.name_en ?? '', sex: 'unknown', dob: '', age_years: '', age_months: '', approximate: false, phone: prefill?.phone ?? '', viber: false, nrc: '', address_line: '', allergies: '', notes: '', consent: false });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [zawgyiNote, setZawgyiNote] = useState(false);
  const [dup, setDup] = useState(false);
  const [more, setMore] = useState(!!initial && !!(initial.nrc || initial.address_line || initial.notes || (initial.allergies?.length ?? 0) > 0));
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });

  function onNameMyBlur() {
    const r = toUnicode(f.name_my);
    if (r.converted) { setF({ ...f, name_my: r.text }); setZawgyiNote(true); }
  }

  async function checkDuplicate() {
    const e164 = normalizePhone(f.phone);
    if (!e164) return;
    const h = await ctx.crypto.index('phone', e164);
    const hit = await ctx.db.getOptional<{ patient_id: string }>('select patient_id from patient_phones where clinic_id = ? and phone_hmac = ?', [ctx.clinicId, h]);
    setDup(!!hit && hit.patient_id !== initial?.id);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!f.name_my.trim() && !f.name_en.trim()) errs.name = t('common.required');
    if (!f.dob && !f.age_years) errs.age = t('patient.ageOrDob');
    if (f.phone && !normalizePhone(f.phone)) errs.phone = t('patient.phoneInvalid');
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const input: NewPatient = {
        honorific: f.honorific === 'none' ? null : f.honorific, name_my: f.name_my || null, name_en: f.name_en || null,
        sex: f.sex as NewPatient['sex'], dob: f.dob || null, age_years: f.age_years ? Number(f.age_years) : null, age_months: f.age_months ? Number(f.age_months) : null,
        age_recorded_on: initial && !f.dob && f.age_years === String(initial.age_years ?? '') ? initial.age_recorded_on ?? null : null,
        age_approximate: f.approximate || (!f.dob && !!f.age_years), nrc: f.nrc || null, address_line: f.address_line || null, notes: f.notes || null,
        phones: f.phone ? [{ phone: f.phone, viber: f.viber }] : [], consent_messaging: f.consent,
        allergies: f.allergies.split(',').map((s) => s.trim()).filter(Boolean).map((s) => ({ substance: s, generic_name: s.toLowerCase() })),
      };
      if (initial) {
        await updatePatient(ctx, initial.id, input);
        onDone(await getPatientIndex(ctx.db, initial.id));
      } else {
        const r = await registerPatient(ctx, input, prefix);
        onDone(await getPatientIndex(ctx.db, r.id));
      }
    } finally { setBusy(false); }
  }

  const nrcParsed = f.nrc ? parseNrc(f.nrc) : null;

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field label={t('patient.honorific')}>
        <Select value={f.honorific} onChange={set('honorific')}>{HONORIFICS.map((h) => <option key={h} value={h}>{t(`honorific.${h}`)}</option>)}</Select>
      </Field>
      <Field label={t('patient.sex')}>
        <Select value={f.sex} onChange={set('sex')}>{['female', 'male', 'other', 'unknown'].map((s) => <option key={s} value={s}>{t(`patient.${s}`)}</option>)}</Select>
      </Field>
      <Field label={t('patient.nameMy')} error={errors.name} hint={zawgyiNote ? t('patient.zawgyiConverted') : undefined}>
        <Input lang="my" className="font-my" value={f.name_my} onChange={set('name_my')} onBlur={onNameMyBlur} autoFocus={!initial} />
      </Field>
      <Field label={t('patient.nameEn')} error={errors.name}><Input value={f.name_en} onChange={set('name_en')} /></Field>
      <Field label={t('patient.dob')} error={errors.age}><Input type="date" value={f.dob} onChange={set('dob')} max={new Date().toISOString().slice(0, 10)} /></Field>
      <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
        <Field label={t('patient.ageYears')} error={errors.age}><Input type="number" min={0} max={130} inputMode="numeric" value={f.age_years} onChange={set('age_years')} disabled={!!f.dob} /></Field>
        <Field label={t('patient.ageMonths')}><Input type="number" min={0} max={11} inputMode="numeric" value={f.age_months} onChange={set('age_months')} disabled={!!f.dob} /></Field>
        <label className="mb-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.approximate} onChange={set('approximate')} className="h-5 w-5" />{t('patient.approximate')}</label>
      </div>
      <Field label={t('patient.phone')} hint={t('patient.phoneHint')} error={errors.phone ?? (dup ? t('patient.duplicateWarning') : null)}>
        <Input type="tel" inputMode="tel" value={f.phone} onChange={set('phone')} onBlur={checkDuplicate} />
      </Field>
      <label className="flex items-center gap-2 self-end pb-3"><input type="checkbox" checked={f.viber} onChange={set('viber')} className="h-5 w-5" />{t('patient.viber')}</label>
      <button type="button" className="text-left text-sm font-semibold text-accent-ink underline sm:col-span-2" onClick={() => setMore(!more)}>{more ? t('patient.lessDetails') : t('patient.moreDetails')}</button>
      {more && (<>
        <Field label={t('patient.nrc')} hint={f.nrc && !nrcParsed ? t('patient.nrcInvalid') : t('patient.nrcHint')}><Input value={f.nrc} onChange={set('nrc')} /></Field>
        <Field label={t('patient.address')}><Input value={f.address_line} onChange={set('address_line')} /></Field>
        <Field label={t('patient.allergies')} hint="Penicillin, NSAIDs …"><Input value={f.allergies} onChange={set('allergies')} /></Field>
        <Field label={t('patient.notes')}><Textarea value={f.notes} onChange={set('notes')} className="min-h-11" /></Field>
        <label className="flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={f.consent} onChange={set('consent')} className="h-5 w-5" />{t('patient.consent')}</label>
      </>)}
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button type="button" onClick={() => onDone(null)}>{t('common.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={busy}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
